/** Human-readable byte size (1024-based). Accepts number or bigint string. */
export function formatBytes(bytes) {
  const value = typeof bytes === 'string' ? Number(bytes) : bytes;
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const size = value / Math.pow(1024, exponent);
  return `${size.toFixed(size >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

/** Exact byte breakdown across units — e.g. "5 TB 10 GB 40 MB" — no rounding within a part. */
export function formatBytesExact(bytes, maxParts = 3) {
  const value = Math.round(typeof bytes === 'string' ? Number(bytes) : bytes);
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = [
    ['TB', 1024 ** 4],
    ['GB', 1024 ** 3],
    ['MB', 1024 ** 2],
    ['KB', 1024],
    ['B', 1],
  ];
  let remaining = value;
  const parts = [];
  for (const [label, size] of units) {
    if (parts.length >= maxParts) break;
    const count = Math.floor(remaining / size);
    if (count > 0) {
      parts.push(`${count} ${label}`);
      remaining -= count * size;
    }
  }
  return parts.length ? parts.join(' ') : '0 B';
}

/** Percentage (0–100) of quota consumed. */
export function quotaPercent(usedBytes, quotaBytes) {
  const used = Number(usedBytes);
  const quota = Number(quotaBytes);
  if (!quota) return 0;
  return Math.min(100, Math.round((used / quota) * 100));
}

/** Locale date-time, defensive against bad input. */
export function formatDate(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * US numeric date, `mm/dd/yyyy`. Signature records are read alongside the
 * stamped PDF and its certificate page, so they use a fixed numeric format
 * rather than a locale-dependent one — the two must never disagree.
 */
export function formatDateNumeric(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/** `mm/dd/yyyy hh:mm AM/PM` — the numeric format above with a local wall clock. */
export function formatDateTimeNumeric(iso) {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

/** Extract a lowercase extension for icon/labeling, '' if none. */
export function fileExtension(name) {
  const idx = name.lastIndexOf('.');
  return idx > 0 ? name.slice(idx + 1).toLowerCase() : '';
}
